// ./src/utils/planOffers.ts
//
// "What should we OFFER to sell this account?" — deliberately NOT the same question as
// `planEntitlement.ts`, which answers "what is this account allowed to do".
//
// ⭐ WHY A SEPARATE FILE. `planEntitlement.ts` is a line-for-line MIRROR of the server's
// `planFeatureGate`, and its value comes entirely from staying one: `npm run check-entitlement`
// exists to prove the two have not drifted. The rule below has **no server counterpart at all** —
// nothing on the server cares which upsell a menu shows — so putting it there would quietly turn
// a mirror into a mirror-plus-some-other-stuff, and the next reader could no longer tell which
// half has to match the API.
//
// Pure, so it can be exercised. `npm run check-entitlement` covers it.
//
// ⚠ THE `BrandId` IMPORT IS `import type` ON PURPOSE — DO NOT MAKE IT A VALUE IMPORT.
// `config/branding.ts` reads `import.meta.env` at module scope, which is `undefined` under
// Node, so importing it for real would crash `npm run check-entitlement` on load — the guard
// that exists to prove this file's rules. A type-only import is erased before it runs.
import { GENERATED_PLAN_CODES as PLAN_CODES } from "@/config/featureCodes.generated";
import type { BrandId } from "@/config/branding";

/**
 * Whether to offer this account an upgrade for ONE EVENT.
 *
 * ⚠ Three states, not a boolean — `unknown` is a real answer and the caller must handle it. The
 * account may not have loaded yet, and a boolean would force that moment to be either "offer" or
 * "hide" before there is anything to decide from.
 */
export type EventUpgradeOffer =
  /** Not on a subscription — a single-event upgrade is a genuine thing to sell them. */
  | "offer"
  /** Already subscribed. Offering a per-event upgrade asks them to buy what they have. */
  | "hide"
  /** The plan has not been read yet. See the caller's note on which way to resolve it. */
  | "unknown";

/**
 * TWO owner rules, one predicate:
 *
 *   1. **A2.9.1 — the per-event upgrade is a PARTYLABZ product.** eventsmagic sells the
 *      subscription; partylabz sells the single event (MONETIZATION A9.2). On the eventsmagic
 *      brand this is never offered, whatever the plan says.
 *   2. **A2.8.3 — never offer a per-event upgrade to a subscriber.** Within partylabz, someone
 *      already paying monthly must not be asked to buy what they have.
 *
 * ⚠ ONE FUNCTION, NOT TWO GATES. The brand check lives here rather than beside the caller
 * because two rules deciding one question is two rules that will eventually disagree — and the
 * one that disagrees silently is the upsell, which produces no error when it is wrong.
 *
 * ⚠ BRAND IS A REQUIRED PARAMETER, WITH NO DEFAULT. A default would let a call site skip the
 * brand and quietly get partylabz behaviour on eventsmagic; required means `tsc` names every
 * caller. This repo has shipped the other version of that mistake before (memory
 * `no-defaults-on-gating-flags`).
 *
 * ⚠ ORDER MATTERS: brand is decided FIRST. A partylabz-only product is not offered on
 * eventsmagic even to a free account — which is exactly the case the plan rule would call
 * "offer".
 *
 * ⚠ ANYTHING THAT IS NOT EXPLICITLY `free` COUNTS AS SUBSCRIBED, including a plan code this
 * build has never heard of. That is the deliberate direction, and it is the OPPOSITE of
 * `evaluateEntitlement`, which fails OPEN when it cannot tell. The two differ because the risk
 * differs: there, failing closed would block a paying customer from something they bought; here,
 * failing open would ask a paying customer to buy what they already have. When in doubt, an
 * entitlement grants and an upsell stays quiet.
 *
 * ⚠ DO NOT USE `AppContext.subscriptionStatus` FOR THIS. It looks like the right signal and is
 * not: its final fallback is `!trialEndsAt ? "active" : "expired"`, so an account that has never
 * had a trial and has no `psSubscriptionStatus` reports **"active"** — which would hide the
 * upgrade from exactly the free users it is meant to be sold to. It describes the trial/billing
 * lifecycle, not the plan tier. The plan code is the authority the server's gate uses, so it is
 * the authority here.
 *
 * ⚠ KNOWN EDGE, ACCEPTED: an account whose subscription has lapsed may still carry a paid
 * `currentPlan`, and will not be offered the event upgrade. That matches what the entitlement
 * gate believes about them, and one surface disagreeing with the gate is worse than one lost
 * upsell.
 */
export const eventUpgradeOffer = (
  currentPlan: string | null | undefined,
  brand: BrandId | null | undefined,
): EventUpgradeOffer => {
  // ⚠ AN UNRECOGNISED BRAND IS `unknown`, WHICH THE CALLER HIDES — the same one-directional
  // reasoning as the plan rule. `BRAND_ID` never actually produces this (an unknown hostname
  // already falls back to eventsmagic, which hides anyway), but a brand read from the API for
  // a white-label domain could, and the safe direction must not depend on today's caller.
  if (brand !== "partylabz" && brand !== "eventsmagic") {
    return "unknown";
  }

  // ⚠ eventsmagic does not sell per-event upgrades AT ALL. This returns before the plan is
  // even looked at, because a free eventsmagic account is precisely the case the plan rule
  // below would answer "offer" — and that is the offer the owner asked to remove.
  if (brand === "eventsmagic") {
    return "hide";
  }

  const code = String(currentPlan ?? "").trim();

  if (!code) {
    return "unknown";
  }

  return code === PLAN_CODES.FREE ? "offer" : "hide";
};

/**
 * Whether to offer this account a SUBSCRIPTION.
 *
 * ⚠ NOT BRAND-GATED, and that is the difference from `eventUpgradeOffer` above. Both brands sell
 * subscriptions — it is eventsmagic's PRIMARY model and partylabz's secondary one, for the
 * frequent host (MONETIZATION A9.2). So the only question here is whether they are already on a
 * paid plan.
 *
 * ⚠ SAME FAIL DIRECTION AS EVERY OTHER UPSELL: anything not explicitly `free` is treated as
 * subscribed, and an unread plan resolves to `unknown`, which the caller hides. Asking a paying
 * customer to subscribe again is the thing to avoid; withholding the prompt for one render costs
 * a free host nothing they cannot get a moment later.
 *
 * ⚠ DO NOT USE `AppContext.subscriptionStatus` HERE EITHER — its fallback reports "active" for a
 * never-trialled free account, which would hide this from exactly the people it is for. The plan
 * code is the authority the server's gate uses, so it is the authority here.
 */
export const subscriptionOffer = (currentPlan: string | null | undefined): EventUpgradeOffer => {
  const code = String(currentPlan ?? "").trim();

  if (!code) {
    return "unknown";
  }

  return code === PLAN_CODES.FREE ? "offer" : "hide";
};

/**
 * ROADMAP #209 — may this plan be offered as a SUBSCRIPTION? partylabz's EVENT plans (VIP, Signature — bought for ONE
 * event, `app_addon_product.plan_id`) carry no price of their own, and must never appear in the plan picker as something
 * to subscribe to. So the picker offers: the free plan (no price rows by design), a plan with a RECURRING price, and the
 * plan the account is on (so it is never hidden from its own subscriber). Pure.
 */
export const isSubscribablePlan = (
  plan: { planCode?: string | null; prices?: ReadonlyArray<{ recurrence?: string | null }> | null },
  /** The account's plan CODE (`account.currentPlan`) — what the picker calls `currentPlanId`. */
  currentPlanCode: string | null | undefined,
): boolean => {
  if (currentPlanCode && String(plan.planCode ?? "").trim() === String(currentPlanCode).trim()) {
    return true;
  }

  if (String(plan.planCode ?? "").trim() === PLAN_CODES.FREE) {
    return true;
  }

  return (plan.prices ?? []).some((p) => !!p?.recurrence && String(p.recurrence).toUpperCase() !== "NONE");
};

/**
 * #60 — the event's plans stage, opened from a page that refused the host: `back` is that page, so closing the drawer
 * (after paying, or not) returns there instead of stranding them on the dashboard. Pure.
 */
export const plansStagePath = (eventId: string, back?: string | null): string => {
  const base = `/events/${eventId}/plans`;
  const from = safePlansBack(eventId, back);

  return from ? `${base}?back=${encodeURIComponent(from)}` : base;
};

/**
 * #60 — where the plans stage may send the host when it closes: ONLY a page of this same event, never the plans stage
 * itself, never the dashboard (closing there is the default anyway). Anything else — another event, another site
 * (`//evil.example`), a scheme — is null, so `?back=` can never be used to redirect anywhere else. Pure.
 */
export const safePlansBack = (eventId: string, back: string | null | undefined): string | null => {
  const path = String(back ?? "").trim();
  const prefix = `/events/${eventId}/`;

  if (!eventId || !path.startsWith(prefix) || path.includes("//") || path.includes("\\")) {
    return null;
  }

  const rest = path.slice(prefix.length).split(/[?#]/)[0];

  if (!rest || rest === "plans" || rest.startsWith("plans/")) {
    return null;
  }

  return path;
};

/** Whether the account-settings form should offer the billing-currency control. */
export type CurrencyChangeHint = "editable" | "locked" | "unknown";

/**
 * Whether to OFFER a billing-currency change — a hint for the form, never the decision.
 *
 * ⚠ THE SERVER DECIDES. ms_auth's `AccountCore.guardCurrencyChange` refuses the change once
 * anything has been charged, and it checks a third signal this function cannot see: whether the
 * account has any purchase in `app_addon_purchase`. A host who bought a top-up without ever
 * subscribing therefore looks `editable` here and is refused on save, with the reason keyed on
 * the `currency` field so it lands under the control.
 *
 * ⚠ THAT LOOSENESS IS THE CORRECT DIRECTION, and it is the one asymmetry worth being explicit
 * about. A client LOOSER than its server offers something that may be refused — a clear message,
 * and nothing wrong is ever written. A client STRICTER than its server hides an action the user
 * is entitled to, with no error anywhere and nothing to search for; ROADMAP A1 is still carrying
 * one of those. So this may over-offer; it must never under-offer.
 *
 * ⚠ NOT `subscriptionOffer` REUSED. The question looks identical and is not: that one asks "are
 * they already paying" (so `unknown` must HIDE, to avoid nagging a customer), while this asks
 * "may they still change this" (so `unknown` must LOCK, because an unread account has not been
 * shown to be payment-free). Both fail safe, in opposite directions — which is exactly why they
 * cannot share an implementation.
 *
 * ⚠ A SUBSCRIPTION ID COUNTS EVEN IF THE PLAN IS BACK TO FREE. A cancelled subscriber keeps the
 * id while `currentPlan` resets, and they have certainly been charged.
 */
export const currencyChangeHint = (
  currentPlan: string | null | undefined,
  psSubscriptionId: string | null | undefined,
): CurrencyChangeHint => {
  if (String(psSubscriptionId ?? "").trim()) {
    return "locked";
  }

  const code = String(currentPlan ?? "").trim();

  if (!code) {
    return "unknown";
  }

  return code === PLAN_CODES.FREE ? "editable" : "locked";
};
